import { BlockSections } from '@/components/builder/inspector-sections';
import { MultiInspector } from '@/components/builder/multi-inspector';
import { BLOCK_ICON } from '@/components/builder/layers-tree';
import { StepSection } from '@/components/builder/step-section';
import { stepBounds } from '@/components/builder/validate';
import { cn } from '@/lib/cn';
import { findNode, findStep, nodeLabel, type AuthoringDoc, type Frame, type SessionStore } from '@personalizehq/editor';
import { MAX_LAYOUT_DEPTH, MAX_LAYOUT_NODES } from '@personalizehq/schemas';
import type { AuthoringBlockNode as BlockNode } from '@personalizehq/schemas';
import { X } from 'lucide-react';
import { useStore } from 'zustand';

// The inspector is now CHROME AND A MAP. Which sections a block gets, and in what order, is a table
// in `inspector-sections.tsx`; the widgets are in `inspector-controls.tsx`. This file used to be
// 1145 lines because all three lived in it.
//
// Editing is not through setters: `applyChange(draft => { node.gap = 8 })` under immer is smaller
// than any setter API and keeps the discriminated union's type safety. The `mergeKey`
// (`${id}.${field}`) is what makes ⌘Z undo a word rather than a letter.

export function Inspector({
  session,
  selectedFrame,
}: {
  session: SessionStore;
  /** The solver's rect for the selected node, lifted out of the artboard. `fill` and `hug` are
   *  promises the SOLVER keeps, and until now the panel could only repeat the promise. */
  selectedFrame?: Frame | null | undefined;
}): React.JSX.Element {
  const doc = useStore(session, (s) => s.doc);
  const selection = useStore(session, (s) => s.selection);
  const selectionAnchor = useStore(session, (s) => s.selectionAnchor);
  const focusedStepId = useStore(session, (s) => s.focusedStepId);

  const step = findStep(doc, focusedStepId);
  const hit = step && selectionAnchor !== null ? findNode(step.root, selectionAnchor) : undefined;
  const bounds = stepBounds(doc, focusedStepId);
  // PARALLAX IS SCROLL-DRIVEN (SPEC §5.0e) and publish rejects it without a scroll above — a law
  // that cannot be seen from the node, only from the tree. So the field is not OFFERED without one:
  // an inspector that lets you author an unpublishable document is an inspector that lies.
  const inScroll = step && selectionAnchor !== null ? hasScrollAncestor(step.root, selectionAnchor) : false;
  const Icon = hit ? BLOCK_ICON[hit.node.block] : null;

  // The draft DOCUMENT is handed to the mutator alongside the node, and it exists for exactly one
  // caller: minting a colour token. A mint writes to `draft.theme` and the assignment writes to the
  // node, and they MUST be the same `applyChange` — ⌘Z has to undo "I used a new colour", not leave
  // an orphan token behind in a theme the author never opened.
  const set = <T extends BlockNode>(field: string, mutate: (node: T, doc: AuthoringDoc) => void): void => {
    session.getState().applyChange((draft) => {
      const target = findNode(findStep(draft, focusedStepId)!.root, selectionAnchor!);
      if (target) mutate(target.node as T, draft);
    }, `${selectionAnchor}.${field}`);
  };

  if (step && selection.length > 1) {
    return <MultiInspector session={session} step={step} selection={selection} />;
  }

  return (
    <div className="flex h-full flex-col">
      {hit ? (
        <>
          <div className="flex h-14 items-center gap-2.5 border-b px-3">
            {Icon ? (
              <span className="grid size-8 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
                <Icon className="size-4" />
              </span>
            ) : null}
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-[13px] font-semibold">{nodeLabel(hit.node, doc.labels)}</div>
              <div className="mt-0.5 text-[10px] uppercase text-muted-foreground">{hit.node.block}</div>
            </div>
            <button
              type="button"
              aria-label="Clear selection"
              title="Clear selection"
              className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => session.getState().select(null)}
            >
              <X className="size-4" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <BlockSections
              node={hit.node}
              parent={hit.parent}
              root={step?.root ?? null}
              labels={doc.labels}
              // The ROOT's height law is the SURFACE's (SPEC §6.1c) — a `fill` root fills the
              // viewport, and only a fullscreen step has one to fill.
              step={step ? { rootId: step.root.id, surface: step.surface, theme: doc.theme } : undefined}
              theme={doc.theme}
              set={set}
              inScroll={inScroll}
              onSelect={(id) => session.getState().select(id)}
              selectedFrame={selectedFrame ?? null}
            />
          </div>
        </>
      ) : (
        // NOT a dead end any more. With nothing selected, the thing you are looking at is the STEP —
        // so this is where its name, its surface and its auto-advance live (B3). `surface` had
        // existed in the schema since P3 with no control anywhere, which is how `inline` — a v1
        // requirement — stayed unauthorable.
        <div className="min-h-0 flex-1 overflow-y-auto">
          <StepSection session={session} />
        </div>
      )}

      {/* The bounds are the publish gate's, shown as a meter rather than discovered as a rejection. */}
      <div
        className={cn(
          'flex h-9 items-center justify-between border-t px-3 text-[10px]',
          bounds.over ? 'text-destructive' : bounds.near ? 'text-amber-500' : 'text-muted-foreground'
        )}
      >
        <span>Document limits</span>
        <span className="tabular-nums">
          {bounds.nodes}/{MAX_LAYOUT_NODES} nodes · {bounds.depth}/{MAX_LAYOUT_DEPTH} depth
        </span>
      </div>
    </div>
  );
}

/** Is there a `scroll` ABOVE this node? Parallax translates against the nearest ancestor scroll's
 *  offset (SPEC §5.0e), so without one the field would do nothing, forever. */
function hasScrollAncestor(root: BlockNode, id: string): boolean {
  const walk = (n: BlockNode, seen: boolean): boolean => {
    if (n.id === id) return seen;
    if (!('children' in n)) return false;
    const next = seen || n.block === 'scroll';
    return n.children.some((c) => walk(c, next));
  };
  return walk(root, false);
}
